import { Template, Component, Attribute } from '@scoutgg/ftw/source/lib';
import { Season, Match } from '../../models';
import '@scoutgg/ftw/source/atoms/select/select';
import Modal from '@scoutgg/ftw/source/services/modal';
import template from './edit-formations.jade';
import PlayerListModal from './player-list-modal/player-list-modal';
import notify from '@scoutgg/ftw/source/services/notification';
import { SCHEMA_VARIANTS } from '@scoutgg/scoring-rules/src/sports/football/fantasy-bet-builder';
import input from '@scoutgg/ftw/source/decorators/input';

@Component('sporty')
@Template(template)
@Attribute('season', Season)
@Attribute('match', Match)
@input('homePlayers', Array)
@input('awayPlayers', Array)
export default class EditFormations extends HTMLElement {
  playerFormations = {};
  formations = {};
  hasPrevFormationData = false;

  async attached() {
    const { season, match } = this;
    if (match && !match.season) match.season = season;
    this.season = season;
    this.match = match;

    this.homeTeamId = match.homeTeam._id;
    this.awayTeamId = match.awayTeam._id;

    const formationData = await this.match.load('formations');

    const homeTeam = formationData.find(
      (el) => el.team._id === this.homeTeamId,
    );
    const awayTeam = formationData.find(
      (el) => el.team._id === this.awayTeamId,
    );

    this.hasPrevFormationData = !!formationData.length;
    this.formations[this.homeTeamId] = homeTeam ? homeTeam.formation : '';
    this.formations[this.awayTeamId] = awayTeam ? awayTeam.formation : '';
    this.playerFormations[this.homeTeamId] = (homeTeam?.players || []).reduce(
      (acc, curr) => {
        acc.set(curr.slot, curr.player._id);

        return acc;
      },
      new Map(),
    );
    this.playerFormations[this.awayTeamId] = (awayTeam?.players || []).reduce(
      (acc, curr) => {
        acc.set(curr.slot, curr.player._id);

        return acc;
      },
      new Map(),
    );

    this.render();
  }

  updateFormation(team, formation) {
    this.formations[team] = formation;
    this.render();
  }

  parseFormation(formation) {
    if (!formation) return [];
    // Parse formation like "4-3-3" into [1, 4, 3, 3]
    return [1, ...formation.split('-').map((num) => parseInt(num))];
  }

  getFormationLines(formation) {
    // Build [[0], [1,2,3,4], [5,6,7], [8,9,10]] structure using reduce
    const { positions } = this.parseFormation(formation).reduce(
      (acc, posCount) => ({
        positions: [
          ...acc.positions,
          Array.from({ length: posCount }, (_, i) => acc.nextIndex + i),
        ],
        nextIndex: acc.nextIndex + posCount,
      }),
      { positions: [], nextIndex: 0 },
    );

    return positions;
  }

  get formationVariants() {
    return SCHEMA_VARIANTS;
  }

  get homeFormationLines() {
    return this.getFormationLines(this.formations[this.homeTeamId]);
  }

  get awayFormationLines() {
    return this.getFormationLines(this.formations[this.awayTeamId]).reverse();
  }

  get isSaveDisabled() {
    return (
      !this.formations[this.awayTeamId] ||
      !this.formations[this.homeTeamId] ||
      this.playerFormations[this.homeTeamId].size < 11 ||
      this.playerFormations[this.awayTeamId].size < 11
    );
  }

  getSelectedPlayer(teamId, slotIndex) {
    if (!teamId || !this.playerFormations[teamId]) return null;

    const playerId = this.playerFormations[teamId].get(slotIndex);

    if (!playerId) return null;

    const players =
      teamId === this.homeTeamId ? this.homePlayers : this.awayPlayers;

    return players?.find((p) => p._id === playerId);
  }

  getSelectedPlayerIds() {
    return Object.values(this.playerFormations).flatMap((teamMap) =>
      Array.from(teamMap.values()),
    );
  }

  removePlayerSelection(teamId, slotIndex, event) {
    event?.stopPropagation();

    if (teamId && this.playerFormations[teamId]) {
      this.playerFormations[teamId].delete(slotIndex);
      this.render();
    }
  }

  onPlayerSelect(teamData, slotIndex, player) {
    if (!player) return;

    const teamId = teamData._id;

    if (teamId && this.playerFormations[teamId]) {
      this.playerFormations[teamId].set(slotIndex, player._id);
      this.render();
    }
  }

  async openPlayerList(team, slotIndex) {
    const players = team === 'home' ? this.homePlayers : this.awayPlayers;
    const teamData =
      team === 'home' ? this.match?.homeTeam : this.match?.awayTeam;

    const playerListModal = PlayerListModal({
      team: teamData,
      players: players || [],
      unavailablePlayerIds: this.getSelectedPlayerIds(),
      onPlayerSelect: (player) =>
        this.onPlayerSelect(teamData, slotIndex, player),
    });

    await Modal(playerListModal, {
      hideCloseBtn: false,
      width: '500px',
    });
  }

  async saveFormations() {
    for (const teamId in this.playerFormations) {
      await this.match[this.hasPrevFormationData ? 'save' : 'post'](
        'formations',
        {
          team: teamId,
          schema: this.formations[teamId],
          players: Array.from(
            this.playerFormations[teamId],
            ([slot, playerId]) => ({
              player: playerId,
              slot: slot,
            }),
          ),
        },
      );
    }

    this.render();
    notify('Formations saved', { level: 'success', timeout: 3000 });
    this.closest('ft-modal')?.remove();
  }
}
